import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { BillingApi } from "@/lib/api/billing-api";
import Toast from "@/lib/components/Toast";
import { queryClient } from "@/lib/queries/query-client";
import { dfyQueryKeys } from "@/lib/queries/dfy-queries";
import { teamQueryKeys } from "@/lib/queries/team-queries";

// Not `payment_status`, which the global credits dialog consumes on every page.
export const DFY_ORDER_SUBMITTED_PARAM = "order_submitted";

export function useSyncDfyOrderAfterSubmit(
  teamExternalId: string | undefined,
  orderExternalId: string | undefined,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasParam = searchParams.get(DFY_ORDER_SUBMITTED_PARAM) === "true";
  const [isSyncing, setIsSyncing] = useState(hasParam);

  useEffect(() => {
    async function syncOrder() {
      if (!hasParam || !teamExternalId || !orderExternalId) {
        return;
      }

      setIsSyncing(true);

      try {
        await BillingApi.syncBilling(teamExternalId);
      } catch (error) {
        console.error(error);
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: dfyQueryKeys.customerOrder(teamExternalId, orderExternalId),
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: teamQueryKeys.getTeam(teamExternalId),
          refetchType: "all",
        }),
      ]);

      Toast.info("Order placed! Our team will get started.");
      setIsSyncing(false);
      setSearchParams(
        (prev) => {
          prev.delete(DFY_ORDER_SUBMITTED_PARAM);
          return prev;
        },
        { replace: true },
      );
    }

    syncOrder();
  }, [hasParam, teamExternalId, orderExternalId, setSearchParams]);

  return isSyncing;
}
